/**
 * ModalSelecionar — modal genérico de pesquisa/seleção
 *
 * Props
 *   open        boolean
 *   onClose     () => void
 *   onSelect    (item) => void
 *   titulo      string                         ex: "Selecionar Imóvel"
 *   endpoint    string                         ex: "/api/imoveis/selecao"
 *   colunas     Array<{
 *                 header: string,
 *                 render: (item) => ReactNode,
 *                 destaque?: boolean,          — texto branco (padrão: cinza)
 *                 maxWidth?: number,           — trunca com ellipsis
 *               }>
 *   placeholder string  (opcional)
 *   maxWidth    "sm"|"md"|"lg"  (opcional, padrão "md")
 *
 * Backend esperado
 *   GET {endpoint}?filtro=&pagina=0&porPagina=10
 *   → { data: any[], total: number }
 *
 * Exemplo de uso
 *   const COLUNAS = [
 *     { header: "Nome", render: (l) => l.nome, destaque: true },
 *     { header: "CPF",  render: (l) => l.cpf },
 *   ];
 *   <ModalSelecionar
 *     titulo="Selecionar Locatário"
 *     endpoint="/api/locatarios/selecao"
 *     placeholder="Nome ou CPF…"
 *     colunas={COLUNAS}
 *     open={open} onClose={onClose} onSelect={onSelect}
 *   />
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

const POR_PAGINA_OPTS = [10, 25, 50];
const BG = "#1e1e1e";
const BG2 = "#252525";
const BORDA = "#2d2d2d";
const TEXTO = "#fff";
const SUB = "#888";

export default function ModalSelecionar({
  open,
  onClose,
  onSelect,
  titulo = "Selecionar",
  endpoint,
  colunas = [],
  placeholder = "Pesquisar…",
  maxWidth = "md",
}) {
  const [inputBusca, setInputBusca] = useState("");
  const [filtroAtivo, setFiltroAtivo] = useState("");
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [carregando, setCarregando] = useState(false);

  const buscar = useCallback(
    (filtro, pag, porPag) => {
      const tk = localStorage.getItem("token");
      setCarregando(true);
      fetch(`${endpoint}?filtro=${encodeURIComponent(filtro)}&pagina=${pag}&porPagina=${porPag}`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => r.json())
        .then((res) => {
          setItens(res.data ?? []);
          setTotal(res.total ?? 0);
        })
        .catch(() => {
          setItens([]);
          setTotal(0);
        })
        .finally(() => setCarregando(false));
    },
    [endpoint],
  );

  useEffect(() => {
    if (!open) return;
    setInputBusca("");
    setFiltroAtivo("");
    setPagina(0);
    buscar("", 0, porPagina);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pesquisar = () => {
    setPagina(0);
    setFiltroAtivo(inputBusca);
    buscar(inputBusca, 0, porPagina);
  };

  const trocarPagina = (_, p) => {
    setPagina(p);
    buscar(filtroAtivo, p, porPagina);
  };

  const trocarPorPagina = (e) => {
    const n = +e.target.value;
    setPorPagina(n);
    setPagina(0);
    buscar(filtroAtivo, 0, n);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: BG,
          backgroundImage: "none",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
          m: 2,
        },
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${BORDA}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: TEXTO, fontWeight: 600, fontSize: "1rem", whiteSpace: "nowrap" }}>{titulo}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: SUB, ml: 2, "&:hover": { color: TEXTO } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo */}
      <Box sx={{ px: 3, pt: 1.5, pb: 1, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {/* Linha de busca — div nativo evita interferência de CSS global em grids */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px", alignItems: "center", marginBottom: "2px" }}>
          <TextField
            autoFocus
            size="small"
            placeholder={placeholder}
            value={inputBusca}
            onChange={(e) => setInputBusca(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && pesquisar()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: SUB, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: BG2,
                "& fieldset": { borderColor: "var(--ig-b-444444)" },
                "&:hover fieldset": { borderColor: "var(--ig-b-666666)" },
                "&.Mui-focused fieldset": { borderColor: "#1976d2" },
              },
              "& input": { color: TEXTO },
              "& input::placeholder": { color: SUB, opacity: 1 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={pesquisar}
            disabled={carregando}
            sx={{ textTransform: "none", px: 2.5, whiteSpace: "nowrap", height: 40 }}
          >
            Pesquisar
          </Button>
        </div>

        {/* Tabela */}
        <TableContainer sx={{ flex: 1, overflow: "auto", border: `1px solid ${BORDA}`, borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {colunas.map((col) => (
                  <TableCell
                    key={col.header}
                    sx={{
                      bgcolor: BG2,
                      color: SUB,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderBottom: `1px solid ${BORDA}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {carregando ? (
                <TableRow>
                  <TableCell colSpan={colunas.length} sx={{ textAlign: "center", py: 5, border: "none", bgcolor: BG }}>
                    <CircularProgress size={24} sx={{ color: SUB }} />
                  </TableCell>
                </TableRow>
              ) : itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colunas.length} sx={{ textAlign: "center", py: 4, border: "none", color: "#555", bgcolor: BG }}>
                    Nenhum resultado encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                itens.map((item, idx) => (
                  <TableRow
                    key={item.id ?? idx}
                    hover
                    onClick={() => {
                      onSelect(item);
                      onClose();
                    }}
                    sx={{
                      cursor: "pointer",
                      bgcolor: BG,
                      "&:hover td": { bgcolor: BG2 },
                      "& td": { borderBottom: `1px solid ${BORDA}` },
                    }}
                  >
                    {colunas.map((col) => (
                      <TableCell
                        key={col.header}
                        sx={{
                          color: col.destaque ? TEXTO : SUB,
                          fontSize: "0.8rem",
                          ...(col.maxWidth
                            ? {
                                maxWidth: col.maxWidth,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }
                            : { whiteSpace: "nowrap" }),
                        }}
                      >
                        {col.render(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={total}
          page={pagina}
          rowsPerPage={porPagina}
          onPageChange={trocarPagina}
          onRowsPerPageChange={trocarPorPagina}
          rowsPerPageOptions={POR_PAGINA_OPTS}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{
            flexShrink: 0,
            color: SUB,
            "& .MuiTablePagination-selectIcon": { color: SUB },
            "& .MuiTablePagination-actions button": { color: SUB },
            "& .MuiTablePagination-actions button.Mui-disabled": { color: BORDA },
            "& p": { color: SUB },
          }}
        />
      </Box>
    </Dialog>
  );
}
